import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/NavLink";
import { Phone } from "lucide-react";

interface UnifiedCTAProps {
  headline: string;
  subtext: string;
  primaryButtonText: string;
  primaryButtonLink?: string;
  primaryButtonHref?: string;
  secondaryButtonText: string;
  secondaryButtonLink?: string;
  secondaryButtonHref?: string;
  showPhoneIcon?: boolean;
  footerText?: string;
}

export const UnifiedCTA = ({
  headline,
  subtext,
  primaryButtonText,
  primaryButtonLink,
  primaryButtonHref,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonHref,
  showPhoneIcon = false,
  footerText,
}: UnifiedCTAProps) => {
  return (
    <section className="py-16 md:py-20 bg-secondary text-white">
      <div className="container-luxury max-w-3xl text-center space-y-6">
        <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-balance">
          {headline}
        </h2>
        <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          {subtext}
        </p>
        
        <div className="flex flex-row items-center justify-center gap-4 pt-2 flex-wrap">
          {/* Primary Button - Solid */}
          {primaryButtonLink ? (
            <Button 
              size="lg"
              asChild 
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 h-12 text-sm sm:text-base font-semibold rounded-full"
            >
              <NavLink to={primaryButtonLink}>
                {showPhoneIcon && <Phone className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />}
                {primaryButtonText}
              </NavLink>
            </Button>
          ) : (
            <Button 
              size="lg"
              asChild 
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 h-12 text-sm sm:text-base font-semibold rounded-full"
            >
              <a href={primaryButtonHref}>
                {showPhoneIcon && <Phone className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />}
                {primaryButtonText}
              </a>
            </Button>
          )}
          
          {/* Secondary Button - Transparent with White Border */}
          {secondaryButtonLink ? (
            <Button 
              size="lg"
              variant="outline"
              asChild 
              className="border-2 border-white/30 text-white hover:bg-white/10 bg-transparent px-6 sm:px-8 h-12 text-sm sm:text-base font-medium rounded-full"
            >
              <NavLink to={secondaryButtonLink}>
                {secondaryButtonText}
              </NavLink>
            </Button>
          ) : (
            <Button 
              size="lg"
              variant="outline"
              asChild 
              className="border-2 border-white/30 text-white hover:bg-white/10 bg-transparent px-6 sm:px-8 h-12 text-sm sm:text-base font-medium rounded-full"
            >
              <a href={secondaryButtonHref} target="_blank" rel="noopener noreferrer">
                {secondaryButtonText}
              </a>
            </Button>
          )}
        </div>
        
        {footerText && (
          <p className="text-white/40 text-sm pt-2">
            {footerText}
          </p>
        )}
      </div>
    </section>
  );
};